import { App, watch } from 'vue';
import { Store } from 'vuex';

import { StateInterface } from 'src/store';
import {
  dismissConnectingNotify,
  dismissReconnectErrorNotify,
  dismissReconnectNotify,
  showConnectingNotify,
  showDisconnectedNotify,
  showReconnectErrorNotify,
  showReconnectNotify,
} from 'src/services/notify';
import {
  getSignInUrl,
  navigateToUrl,
  storeRedirectPath,
} from 'src/services/routing';
import { removeToken } from 'src/services/token';
import { ApiClient } from 'src/api/ws-clilent';
import { WSP } from 'src/api/wsp';
import { WsHandler } from 'src/api/ws-handler';

const MAX_CONNECTION_LIMIT = 3;

let $app: App;
let $client: ApiClient | null;
let connectionAttempts = 0;
let connectionTimeout: number;

export const setApp = (app: App) => {
  $app = app;
};

export const setApiClient = (client: ApiClient) => {
  $client = client;
};

const tryToConnectHandler = () => {
  connectionAttempts = 0;
  connectWS();
};

export function connectWS() {
  if (process.env.SERVER) {
    console.info('PREVENT CONNECTING WS ON SERVER SIDE', connectionAttempts);
    return;
  }

  showConnectingNotify();

  connectionAttempts++;

  process.env.DEBUGGING && console.info('CONNECTING...', connectionAttempts);

  $app.config.globalProperties.$connect();
}

export function disconnectWS() {
  process.env.DEBUGGING && console.info('DISCONNECT;', connectionAttempts);

  clearTimeout(connectionTimeout);
  dismissReconnectNotify();
  dismissConnectingNotify();

  $app.config.globalProperties.$disconnect();
  connectionAttempts = 0;
}

const refreshAuth = () => {
  const appHost = $app.config.globalProperties.$store.state.app.host as string;
  const authUrl = getSignInUrl(appHost);

  removeToken(appHost);
  storeRedirectPath();

  return navigateToUrl(authUrl);
};

export function onWSClose(e: CloseEvent) {
  clearTimeout(connectionTimeout);

  $client = null; // workaround for WSP: force new instance after reconnection

  if (e.code === 1005) {
    process.env.DEBUGGING && console.info('WS CLOSED MANUALLY(?);');

    return;
  }

  process.env.DEBUGGING && console.info('WS CLOSED;', connectionAttempts);

  if (connectionAttempts >= MAX_CONNECTION_LIMIT) {
    if (process.env.DEBUGGING) {
      dismissConnectingNotify();
      return showReconnectErrorNotify(MAX_CONNECTION_LIMIT, refreshAuth);
    }

    return refreshAuth();
  }

  const user = $app.config.globalProperties.$store.state.app.user;

  if ((e.code === 1006 || e.code === 1001) && user) {
    connectionTimeout = window.setTimeout(connectWS, 1000);
    return;
  }

  return showDisconnectedNotify(
    `Error code: ${e.code}`,
    refreshAuth,
    tryToConnectHandler
  );
}

export function onWSOpen() {
  dismissConnectingNotify();
  dismissReconnectNotify();
  // todo: refresh data on (re)connection
  // loadData() import from './api?';
  connectionAttempts = 0;
}

// export function onWSError() {}

export function onWSReconnect() {
  dismissReconnectErrorNotify();
  dismissConnectingNotify();
  showReconnectNotify();
}

export function onWSReconnectError() {
  dismissReconnectNotify();
  dismissConnectingNotify();
  showReconnectErrorNotify(MAX_CONNECTION_LIMIT, tryToConnectHandler);
}

// used in boot/connect to init VuexNativeWebsocket
export function getOptions(app: App, store: Store<StateInterface>) {
  setApp(app);

  const WS_HOST = process.env.WS_HOST;
  const AUTH_TOKEN = process.env.WS_AUTH_TOKEN_NAME;
  return {
    url: `${WS_HOST}?idToken=${AUTH_TOKEN}`,
    options: {
      store,
      format: 'json',
      connectManually: true,
      reconnection: false,
      reconnectionAttempts: 3, // comment to set INFINITY
      reconnectionDelay: 2000,
      passToStoreHandler: WsHandler.create(store),
    },
  };
}

export function useApiClient<T = void>(
  useFn: (client: ApiClient) => Promise<T>
) {
  if ($client) {
    return Promise.resolve(useFn($client));
  }
  return getApiClient().then(useFn);
}

export function getApiClient() {
  if ($client) {
    return Promise.resolve($client);
  }

  return getSocket().then((socket) => {
    const wsp = WSP.create(socket);
    $client = new ApiClient(wsp);
    return $client;
  });
}

export function getSocket(
  store: Store<StateInterface> | null = null
): Promise<WebSocket> {
  const $store = store || $app.config.globalProperties.$store;

  if ($store.state.socket.isConnected) {
    return Promise.resolve($app.config.globalProperties.$socket as WebSocket);
  }

  return new Promise<WebSocket>((resolve, reject) => {
    const rejectTimeout = setTimeout(() => {
      reject('getSocket: Connection timeout');
    }, 30000);

    const unwatch = watch(
      () => $store.state.socket.isConnected,
      (isConnected) => {
        if (isConnected) {
          clearTimeout(rejectTimeout);
          resolve($app.config.globalProperties.$socket as WebSocket);
          unwatch();
        } else if (process.env.DEBUGGING) {
          console.warn('getSocket: Disconnected on wait?');
        }
      }
    );
  });
}
